import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "antd";
import { matchPath, useLocation } from "react-router-dom";
import assistantIcon from "../../assets/gifs/Ai Robot Vector Art.gif";
import { routes } from "../../routes";
import { useTheme } from "../../utils/ThemeContext";
import ROUTES from "../../config/routes";
import {
  assistantPromptLibrary,
  buildAssistantReply,
  buildRouteMeta,
} from "./AppAssistantConfig";
import { setIsOpen, addMessage, clearMessages, setExternalQuery } from "../../redux/slices/aiAssistantSlice";
import "./AppAssistant.css";

const ASSISTANT_API_URL = `${ROUTES.BASE_URL}/auth-service/v1/chat/completions`;

const createInitialMessages = (routeLabel) => [
  {
    id: "assistant-welcome",
    role: "assistant",
    text: `Welcome! I'm Assetra AI, your personal financial co-pilot. I am here to provide wealth management guidance and answers to your complex financial questions. How can I assist you with your wealth journey today?`,
    timestamp: new Date().toISOString(),
  },
];

export const AppAssistant = () => {
  const { theme } = useTheme();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isOpen = false, messages = [], externalQuery = null } = useSelector((state) => (state.rootReducer && state.rootReducer.aiAssistant) || state.aiAssistant || {});
  
  const scrollRef = useRef(null);
  const timerRef = useRef(null);
  
  const [draft, setDraft] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [streamedResponse, setStreamedResponse] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);

  const matchedRoute = useMemo(() => {
    return routes.find((route) =>
      matchPath(location.pathname, {
        path: route.path,
        exact: true,
      })
    );
  }, [location.pathname]);

  const routeMeta = useMemo(() => {
    return buildRouteMeta(matchedRoute, location.pathname);
  }, [matchedRoute, location.pathname]);

  const shouldHideAssistant = useMemo(() => {
    const hiddenRouteNames = [
      "Login",
      "Signup",
      "Register",
      "Otp",
      "TrusteeLogin",
      "Forget Password",
      "Accept Invitation",
      "Confirm",
      "Payment",
      "Free Trial",
      "Delegate Access",
      "Page Not Found",
    ];

    const hiddenPathPrefixes = [
      "/login",
      "/sign-up",
      "/signup",
      "/acceptInvitation",
      "/payment",
      "/paymentv2",
      "/free-trial",
      "/forget-password",
      "/otpvalidation",
      "/trusteeLogin",
      "/confirm",
      "/delgateAccess",
      "/subscription",
    ];

    if (matchedRoute && hiddenRouteNames.includes(matchedRoute.name)) {
      return true;
    }

    return hiddenPathPrefixes.some((prefix) =>
      location.pathname.startsWith(prefix)
    );
  }, [location.pathname, matchedRoute]);

  const quickPrompts = useMemo(() => {
    return (
      assistantPromptLibrary[routeMeta.key] ||
      assistantPromptLibrary.default
    );
  }, [routeMeta.key]);

  const addAssistantResponse = (text) => {
    dispatch(addMessage({
      id: `assistant-${Date.now()}`,
      role: "assistant",
      text,
      timestamp: new Date().toISOString(),
    }));
  };

  const respondWithLocalGuidance = (message, includeConnectivityNote = false) => {
    const fallbackReply = buildAssistantReply({
      message,
      routeLabel: routeMeta.label,
      routeSummary: routeMeta.summary,
    });

    const replyText = includeConnectivityNote
      ? `I could not reach the live assistant service, so I switched to built-in guidance.\n\n${fallbackReply}`
      : fallbackReply;

    setIsTyping(false);
    setIsStreaming(false);
    setStreamedResponse("");
    addAssistantResponse(replyText);
  };

  useEffect(() => {
    if (!messages || !messages.length) {
      dispatch(clearMessages(createInitialMessages(routeMeta.label)));
    }
  }, [messages, routeMeta.label, dispatch]);

  useEffect(() => {
    if (!scrollRef.current) {
      return;
    }

    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isTyping, isOpen, streamedResponse]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen && !shouldHideAssistant) {
      setHasUnread(true);
    }
  }, [location.pathname, isOpen, shouldHideAssistant]);

  // Message history dispatch is handled directly now

  const submitMessage = async (incomingMessage) => {
    const nextMessage = (incomingMessage || draft).trim();

    if (!nextMessage) {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    dispatch(addMessage({
      id: `user-${Date.now()}`,
      role: "user",
      text: nextMessage,
      timestamp: new Date().toISOString(),
    }));

    setDraft("");
    setIsTyping(true);

    try {
      const chatMessages = [
        {
          role: "system",
          content:
            "You are 'Assetra AI', an expert AI financial advisor and wealth management assistant. You possess comprehensive knowledge of US finance, tax planning, asset allocation, and personal wealth building strategies. Your primary goal is to provide highly professional, accurate, and actionable financial guidance. Respond to the user's queries in a supportive, objective, and highly capable tone. Keep your responses concise, well-structured, and easy to understand using markdown formatting where appropriate. Do not discuss internal application code or return raw JSON. Act as a trusted partner to help the user navigate their financial journey with clarity and confidence.",
        },
        ...messages.map((m) => ({ role: m.role, content: m.text })),
        { role: "user", content: nextMessage },
      ];

      if (!ASSISTANT_API_URL) {
        respondWithLocalGuidance(nextMessage);
        return;
      }

      const userToken = localStorage.getItem("userLoginToken");
      const headers = {
        "Content-Type": "application/json",
      };

      if (userToken) {
        headers.Authorization = `Bearer ${userToken}`;
      }

      const response = await fetch(ASSISTANT_API_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({
          model: "openai/gpt-oss-120b",
          messages: chatMessages,
          temperature: 1,
          top_p: 1,
          max_tokens: 4096,
          stream: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let fullText = "";
      setIsStreaming(true);
      setStreamedResponse("");

      let done = false;
      let buffer = "";
      let lastUpdateTime = Date.now();
      let firstChunkReceived = false;

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop(); // save incomplete line back to buffer
          
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();
            if (line === "") continue;
            
            if (line === "data: [DONE]") {
              done = true;
              break;
            }
            if (line.startsWith("data: ")) {
              try {
                const parsed = JSON.parse(line.substring(6));
                const content = (parsed && parsed.choices && parsed.choices[0] && parsed.choices[0].delta && parsed.choices[0].delta.content) || "";
                if (content) {
                  fullText += content;
                  
                  if (!firstChunkReceived) {
                    setIsTyping(false);
                    firstChunkReceived = true;
                  }

                  const now = Date.now();
                  if (now - lastUpdateTime > 80) {
                    setStreamedResponse(fullText);
                    lastUpdateTime = now;
                  }
                }
              } catch (parseError) {
                // ignore parse errors for partial chunks
              }
            }
          }
        }
      }
      
      setStreamedResponse(fullText);
      setIsStreaming(false);
      setIsTyping(false);
      addAssistantResponse(fullText);
      setStreamedResponse("");

    } catch (e) {
      console.log(e);
      respondWithLocalGuidance(nextMessage, true);
    }
  };

  useEffect(() => {
    if (externalQuery) {
      submitMessage(externalQuery);
      dispatch(setExternalQuery(null));
    }
  }, [externalQuery]);

  const clearHistory = () => {
    dispatch(clearMessages(createInitialMessages(routeMeta.label)));
    setIsTyping(false);
  };

  const toggleAssistant = () => {
    dispatch(setIsOpen(!isOpen));
    setHasUnread(false);
  };

  if (shouldHideAssistant) {
    return null;
  }

  return (
    <div
      className={`app-assistant-shell ${
        theme === "dark" ? "app-assistant-shell--dark" : ""
      }`}>
      <button
        type="button"
        className="app-assistant-launcher"
        onClick={toggleAssistant}>
        <span className="app-assistant-launcher__glow" />
        <span className="app-assistant-launcher__icon">
          <img src={assistantIcon} alt="Assetra AI" />
        </span>
        <span className="app-assistant-launcher__copy">
          <strong>Assetra AI</strong>
          <span>{isOpen ? "Tap to minimize" : "Tap to open"}</span>
        </span>
        {hasUnread && <span className="app-assistant-launcher__badge" />}
      </button>

      <div
        className={`app-assistant-panel ${
          isOpen ? "app-assistant-panel--open" : ""
        }`}>
        <div className="app-assistant-panel__aurora" />

        <div className="app-assistant-panel__header">
          <div className="app-assistant-panel__identity">
            <div className="app-assistant-panel__avatar">
              <img src={assistantIcon} alt="Assetra AI" />
            </div>
            <div>
              <h3>Assetra AI</h3>
              <p style={{ fontSize: '11px', margin: 0, opacity: 0.8 }}>Powered by Asset Planet</p>
            </div>
          </div>

        <div className="app-assistant-panel__actions">
            {/*<button type="button" onClick={clearHistory} title="Clear history">
              <Icon type="delete" />
            </button>*/}
            <button type="button" onClick={toggleAssistant} title="Minimize">
              <Icon type="minus" />
            </button>
          </div> 
        </div>

       {/* <div className="app-assistant-panel__context">
          <span className="app-assistant-panel__pill">
            Active in {routeMeta.label}
          </span>
          <span className="app-assistant-panel__status">
            <span className="app-assistant-panel__status-dot" />
            History stays while you move pages
          </span>
        </div> */}

      {/*<div className="app-assistant-panel__prompts">
          {quickPrompts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              className="app-assistant-panel__prompt"
              onClick={() => submitMessage(prompt)}>
              {prompt}
            </button>
          ))}
        </div> */}

        <div ref={scrollRef} className="app-assistant-panel__messages">
          {messages && messages.map((message) => (
            <div
              key={message.id}
              className={`app-assistant-message app-assistant-message--${message.role}`}>
              <div className="app-assistant-message__label">
                {message.role === "assistant" ? "Assetra AI" : "You"}
              </div>
              <div className="app-assistant-message__bubble">
                {message.role === "assistant" ? (
                  <div className="app-assistant-message__markdown">
                    {message.text}
                  </div>
                ) : (
                  message.text
                )}
              </div>
            </div>
          ))}

          {isStreaming && streamedResponse && (
            <div className="app-assistant-message app-assistant-message--assistant">
              <div className="app-assistant-message__label">Assetra AI</div>
              <div className="app-assistant-message__bubble">
                <div className="app-assistant-message__markdown">
                  {streamedResponse}
                </div>
              </div>
            </div>
          )}

          {isTyping && (
            <div className="app-assistant-message">
              <div className="app-assistant-message__label">Assetra AI</div>
              <div className="app-assistant-message__bubble app-assistant-message__bubble--typing">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}
        </div>

        <div className="app-assistant-panel__composer">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submitMessage();
              }
            }}
            placeholder="Ask your financial assistant anything..."
          />
          <button type="button" onClick={() => submitMessage()}>
            <Icon type="arrow-right" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppAssistant;
